import { AutoSaveIndicator, Button, Css, ModalBody, ModalFooter, ModalHeader, useModal } from "@homebound/beam";
import { useNavigate } from "react-router-dom";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PlanPackageTakeoffPage_TakeoffDetailFragment } from "src/routes/libraries/plan-package/takeoffs/PlanPackageTakeoffPage.generated";
import { createPlanPackageIncomingSlotReviewUrl } from "src/RouteUrls";
import { fail, pluralize } from "src/utils";
import { PublishAggregateVersionModal } from "../../details/components/PublishAggregateVersionModal";

export type TakeoffHeaderProps = {
  planPackage: PlanPackageTakeoffPage_TakeoffDetailFragment;
  pendingSlotCount: number;
};

export function TakeoffsHeader(props: TakeoffHeaderProps) {
  const { planPackage, pendingSlotCount } = props;
  const { openModal } = useModal();
  const navigate = useNavigate();
  const designerModifiedSlotsEnabled = useFeatureFlag(FeatureFlagType.DesignerModifiedSlots);

  const { aggregateDraft, name } = planPackage;
  const canPublish = aggregateDraft?.canPublish.allowed;
  const disabledReasons = aggregateDraft?.canPublish.disabledReasons ?? [];
  // Show the Publish button if they can publish, or if they cannot publish but there are no issues - in which case it'll be disabled.
  // Otherwise, we'd show a "View Issues" button that opens an empty modal, which is more confusing than helpful.
  const showPublishButton = canPublish || (!canPublish && disabledReasons.length === 0);

  return (
    <PageHeader
      xss={Css.bgTransparent.bn.mb0.$}
      titleSize="xl2"
      title="Takeoff Manager"
      left={<AutoSaveIndicator />}
      right={
        <>
          {designerModifiedSlotsEnabled && pendingSlotCount > 0 && (
            <Button
              label={`${pendingSlotCount} ${pluralize(pendingSlotCount, "slot")} to review`}
              onClick={() =>
                navigate(
                  createPlanPackageIncomingSlotReviewUrl(
                    planPackage.id,
                    planPackage.version?.id ?? fail("Plan package version is required"),
                  ),
                )
              }
              variant="caution"
              icon="todo"
            />
          )}
          <ReadyPlanVersionHeader readyPlan={planPackage} versionId={planPackage.version?.id} />
          {/* TODO: Work with Product & Design to determine when/how this can be Published */}
          {showPublishButton && (
            <Button
              size="md"
              disabled={!canPublish}
              tooltip={disableBasedOnPotentialOperation(aggregateDraft?.canPublish)}
              label="Publish"
              onClick={() =>
                openModal({
                  content: (
                    <PublishAggregateVersionModal
                      draftAggregateVersion={aggregateDraft ?? fail("No draft aggregate version found")}
                      copyText="This update will be made available to any Design Package and Product Offering that reference it."
                      readyPlanName={name}
                    />
                  ),
                })
              }
            />
          )}
          {!showPublishButton && (
            <Button
              size="md"
              label="View Issues"
              onClick={() => openModal({ content: <DisabledPublishReasons aggregateDraft={aggregateDraft} /> })}
            />
          )}
        </>
      }
    />
  );
}

function DisabledPublishReasons({
  aggregateDraft,
}: {
  aggregateDraft?: PlanPackageTakeoffPage_TakeoffDetailFragment["aggregateDraft"];
}) {
  const { closeModal } = useModal();
  const reasons = aggregateDraft?.canPublish.disabledReasons ?? [];
  return (
    <>
      <ModalHeader>Takeoff Issues</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          {reasons.map((reason) => (
            <pre key={reason.message} css={Css.gray800.m0.add("whiteSpace", "pre-wrap").sm.$}>
              {reason.message}
            </pre>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Close" onClick={() => closeModal()} />
      </ModalFooter>
    </>
  );
}
